import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Upload, X, Loader2, Image as ImgIcon, Video as VideoIcon } from "lucide-react";

export function ImageUploader({ value, onChange, label = "Image", testId }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    setBusy(true);
    setErr("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.data_url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-2">{label}</div>
      <div className="flex items-start gap-3">
        <div className="relative w-28 h-28 border border-[#2A2A2A] bg-[#0A0A0A] flex items-center justify-center overflow-hidden shrink-0">
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImgIcon className="w-6 h-6 text-white/30" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="btn-outline text-xs px-4 py-2"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {busy ? "Uploading…" : value ? "Replace image" : "Upload image"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-xs text-white/45 hover:text-red-400 flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Remove
            </button>
          )}
        </div>
      </div>
      {err && <div className="text-xs text-red-400 mt-2">{err}</div>}
    </div>
  );
}

export function VideoUploader({ value, onChange, label = "Video", testId, helperText }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const handleFile = async (file) => {
    if (!file) return;
    // Client-side pre-check so users don't waste bandwidth on rejected uploads.
    if (file.size > 30 * 1024 * 1024) {
      setErr("Video too large (max 30MB). Trim it and try again.");
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/upload-video", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.video_url || data.data_url);
    } catch (e) {
      setErr(e.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testId}>
      <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-2">{label}</div>
      <div className="flex items-start gap-3">
        <div className="relative w-28 h-28 border border-[#2A2A2A] bg-[#0A0A0A] flex items-center justify-center overflow-hidden shrink-0">
          {value ? (
            <video src={value} className="w-full h-full object-cover" muted playsInline />
          ) : (
            <VideoIcon className="w-6 h-6 text-white/30" />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input
            ref={fileRef}
            type="file"
            accept="video/*"
            className="hidden"
            onChange={(e) => handleFile(e.target.files?.[0])}
          />
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="btn-outline text-xs px-4 py-2"
          >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
            {busy ? "Uploading…" : value ? "Replace video" : "Upload video"}
          </button>
          {value && (
            <button
              type="button"
              onClick={() => onChange("")}
              className="text-xs text-white/45 hover:text-red-400 flex items-center gap-1"
            >
              <X className="w-3 h-3" /> Remove
            </button>
          )}
          {helperText && !err && (
            <div className="text-[0.65rem] text-white/40 leading-relaxed max-w-[220px]">{helperText}</div>
          )}
        </div>
      </div>
      {err && <div className="text-xs text-red-400 mt-2">{err}</div>}
    </div>
  );
}

export function Field({ label, children }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-[0.22em] text-white/55 mb-2">{label}</div>
      {children}
    </div>
  );
}

export function Input({ value, onChange, type = "text", placeholder, testId, min, max, step }) {
  return (
    <input
      type={type}
      value={value === undefined || value === null ? "" : value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      min={min}
      max={max}
      step={step}
      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-white text-sm"
      data-testid={testId}
    />
  );
}

export function Textarea({ value, onChange, rows = 4, testId }) {
  return (
    <textarea
      rows={rows}
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-white text-sm leading-relaxed"
      data-testid={testId}
    />
  );
}

export function Select({ value, onChange, options, testId }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0A0A0A] border border-[#2A2A2A] focus:border-[#D4AF37] outline-none px-3 py-2.5 text-white text-sm"
      data-testid={testId}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}
